import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps { }

const mapStateToProps = (state: CustomApplicationState) => {
    const myPurchaseHistoryState = state.ADIGlobalData.myPurchaseHistoryStore.myPurchaseHistory;
    const currentSplitPath = getLocation(state).pathname.split("/");
    const currentPath = currentSplitPath[currentSplitPath.length - 1];
    return {
        currentPage: currentPath,
        pagination: myPurchaseHistoryState?.pagination,
        totalItemCount: myPurchaseHistoryState?.pagination?.totalItemCount,
        query: myPurchaseHistoryState?.searchText,
        searchfromDate: myPurchaseHistoryState?.fromDate,
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface PurchaseHistoryProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    queryHeading?: InjectableCss;
    rppContainer?:InjectableCss;
    rppInnerContainer?:InjectableCss;
}

const PurchaseHistoryProductCount: FC<Props> = ({ currentPage, pagination, totalItemCount, query, searchfromDate }) => {
    if (!totalItemCount) {
        return null;
    }

    const purchaseHistoryProductCountStyles: PurchaseHistoryProductCountStyles = {
        wrapper: {
            css: css`
            padding: 0px 16px;
            font-size: 14px;
            margin-left: ${currentPage === "shopmybranch" ? "" : "0px"};
            ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px;
                        `,
                        css`
                            padding: 0px;
                        `,
                        css`
                            padding-left: ${currentPage === "shopmybranch" ? "0px" : ""};
                        `,
                        null,
                        null,
                    ])}
        `,
        },
        countText: {
            css: css`
            white-space: nowrap;
            ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                white-space: normal;
                            `,
                            css`
                                white-space: normal;
                            `,
                            null,
                            null,
                            null,
                        ])}
        `,
        },
        queryText: {
            css: css`
            white-space: nowrap;
            font-weight: bold;
        `,        
        },
        queryHeading: {
            css: css`
            font-size: 16px;
            font-weight: 900;
            line-height: normal;
            color: #002856;

            ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                margin-bottom: 10px;
                                display: block;
                                font-size: 20px;
                            `,
                            css`
                                margin-bottom: 10px;
                                display: block;
                                font-size: 20px;
                            `,
                            null,
                            null,
                            null,
                        ])}
            
        `,      
        },
        rppContainer: {
            css: css`
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            padding:11px 16px 12px 20px;

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    flex-direction: column;
                    padding-left: 0;
                    align-items: flex-start;
                `,
                css`
                    flex-direction: column;
                    padding-left: 0;
                    align-items: flex-start;
                `,
                css`
                    padding-left: ${currentPage === "shopmybranch" ? "0px" : ""};
                `,
                null,
                null,
            ])}
            
        `,      
        },
        rppInnerContainer: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        white-space: nowrap;
                        max-width: 100%;
                        flex-basis: 100%;
                        margin-bottom: 10px;
                    `,
                    css`
                        white-space: nowrap;
                        max-width: 100%;
                        flex-basis: 100%;
                        margin-bottom: 10px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,      
        },
    };

    const styles = purchaseHistoryProductCountStyles;

    const startPageOption = () => {
        if (!pagination?.page) {
            return;
        }
        if (pagination?.page == 1) {
            return 1;
        }
        else {
            return (Number(pagination?.page - 1) * Number(pagination?.pageSize)) + 1;
        }
    };

    const endPageOption = () => {
        if (!pagination?.totalItemCount) {
            return
        }
        const pageSizeConst = (Number(pagination?.pageSize) % Number(pagination?.defaultPageSize) == 0) ? Number(pagination?.pageSize) : Number(pagination?.pageSize) + 1;

        const countNumber = Number(pagination?.page) * Number(pageSizeConst);
        if (countNumber < pagination?.totalItemCount) {
                return (Number(pagination?.page) * Number(pagination?.pageSize));
        }
        else {
            return pagination?.totalItemCount
        }
    };

    return (
    <StyledWrapper {...styles.rppContainer}>
        {currentPage !== "shopmybranch" && (
        <StyledWrapper {...styles.rppInnerContainer}>
            <Typography {...styles.queryHeading} data-test-selector="purchaseHistoryHeadingText">
                {query != ""  ? "Search Results" : searchfromDate != null ? "Search Results" : "Recently Purchased" }
            </Typography>
        </StyledWrapper>
        )}
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.countText} data-test-selector="purchaseHistoryCountText">
                <StyledWrapper>{`Showing ${startPageOption()} - ${endPageOption()} of ${pagination?.totalItemCount}`}</StyledWrapper>
            </Typography>
            
        </StyledWrapper>
    </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PurchaseHistoryProductCount),
    definition: {
        group: "My Account",
        displayName: "Purchase History Product Count",
    },
};

export default widgetModule;
